import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
// services
import { accountingCenterService } from '@/services/accounting-centers.service';

// utils
import { calculateChartData, getBtevChartConfig, getChartConfigs } from '@/utils/chartCalculations';

// components
import { Accordion } from '@/components/ui/accordion';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import { Button } from '@/components/ui/button';
import ACItemAccordion from '@/components/molecules/ACItemAccordion';

// icons
import editIcon from '@/assets/icons/edit.svg';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import infoIcon from '@/assets/icons/info.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import invoicePrimaryIcon from '@/assets/icons/invoice-primary.svg';
import invoiceWhiteIcon from '@/assets/icons/invoice-white.svg';
import workActivityIcon from '@/assets/icons/work-activity-primary.svg';
import workActivityWhiteIcon from '@/assets/icons/work-activity-white.svg';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import noWorkActivityIcon from '@/assets/icons/no-work-activity.svg';
import departmentACIcon from '@/assets/icons/department-acs.svg';
import productsACIcon from '@/assets/icons/product-and-services.svg';
import projectsACIcon from '@/assets/icons/project-acs.svg';
import contractsACIcon from '@/assets/icons/contracts-acs.svg';

import acExpanatoryImg from '@/assets/images/ac-explanatory-1.png';
import acExpanatoryImg2 from '@/assets/images/ac-explanatory-2.png';
import acExpanatoryImg3 from '@/assets/images/ac-explanatory-3.png';

import { PAGE } from '@/routes';
import ExplanatoryModal from '@/components/molecules/ExplanatoryModal';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import { useDeviceType } from '@/hooks/useDeviceType';
import { Invoice } from '@/services/types/invoice.types';
import { invoiceService } from '@/services/invoice.service';
import { workActivityService } from '@/services/work-activity.service';
import TabPills from '@/components/molecules/TabPills';
import InvoicesTable from '@/components/molecules/invoice/InvoicesTable';
import WorkActivityTable from '@/components/molecules/workActivity/WorkActivityTable';
import { WorkActivity } from '@/services/types/work-activity.types';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';

type ACType = 'DEPARTMENT_ACCOUNTING' | 'PRODUCTS_AND_SERVICES' | 'PROJECTS' | 'CONTRACTS';

const ACCOUNTING_CENTERS_ICONS: Record<ACType, string> = {
  DEPARTMENT_ACCOUNTING: departmentACIcon,
  PRODUCTS_AND_SERVICES: productsACIcon,
  PROJECTS: projectsACIcon,
  CONTRACTS: contractsACIcon
};

const steps = [
  {
    title: 'Understanding MAEs',
    message:
      'MAEs are customizable entities designed for better cashflow management. They are categorized into four types: Departments, Products & Services, Projects, and Contract Entities.',
    img: acExpanatoryImg
  },
  {
    title: 'Setting Limits with ACMEV and ACRT',
    message:
      'ACMEV is the maximum spending limit for each AC. ACRT is the revenue target assigned to each AC. Both values are aligned with the overall BMEV and BTRT.',
    img: acExpanatoryImg2
  },
  {
    title: 'Linking Invoices to MAEs',
    message:
      'Invoices can be allocated to specific MAEs, providing detailed insights into the financial activities of each category.',
    img: acExpanatoryImg3
  }
];

type ContentTab = 'invoice' | 'work-activity';

const AccountingCentersPage = () => {
  const [graphViewMode, setGraphViewMode] = useState('percentage');
  const [activeContentTab, setActiveContentTab] = useState<ContentTab>('invoice');
  const [accountCenters, setAccountingCenters] = useState<any[]>([]);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [totalBudget, setTotalBudget] = useState({
    acmev: 0,
    acmev_used: 0,
    acoh: 0,
    acoh_used: 0,
    acrt: 0,
    acrt_achieved: 0,
    type: '' as ACType
  });

  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [activeInvoiceTab, setActiveInvoiceTab] = useState('All');
  const [defaultAC, setDefaultAC] = useState<any | undefined>(undefined);
  const [workActivities, setWorkActivities] = useState<WorkActivity[]>([]);
  const [loadingWorkActivities, setLoadingWorkActivities] = useState(false);
  const [totalWorkActivityCount, setTotalWorkActivityCount] = useState(0);

  const { budgetId, acType } = useParams();

  const navigate = useNavigate();
  const { isMobileView } = useDeviceType();

  const location = useLocation();
  const acCenter = location.state?.acCenter as any | undefined;
  const budget = location.state?.budget as any | undefined;

  const { can } = useBudgetPermissions({
    budgetId,
    fallbackMembers: budget?.members ?? []
  });
  const canCreateInvoice = can('invoice:create');
  const canCreateAccountingCenter = can('accountingCenter:create');
  const canAdjustAccountingCenter = can('accountingCenter:adjust');

  const handleInvoiceSelect = (invoice: Invoice & { budget?: any }) => {
    if (budgetId) {
      navigate(`${PAGE.BUDGETS.path}/${budgetId}/invoices/${invoice._id}`, {
        state: { budget, from: 'accountingCenter', acType }
      });
    }
  };

  const fetchAccountCenterDetails = async () => {
    // Fetch accounting center details
    try {
      const { data: accountingCentersData } = await accountingCenterService.getAccountingCentersByType(
        budgetId || '',
        String(acType).toUpperCase() || ''
      );

      // Calculate totals for all accounting centers
      const totals = accountingCentersData.reduce(
        (acc, ac) => ({
          acmev: acc.acmev + (ac.acmev || 0),
          acmev_used: acc.acmev_used + (ac.acmev_used || 0),
          acoh: acc.acoh + (ac.acoh || 0),
          acoh_used: acc.acoh_used + (ac.acoh_used || 0),
          acrt: acc.acrt + (ac.acrt || 0),
          acrt_achieved: acc.acrt_achieved + (ac.acrt_achieved || 0)
        }),
        { acmev: 0, acmev_used: 0, acoh: 0, acoh_used: 0, acrt: 0, acrt_achieved: 0 }
      );
      console.log('totals', totals);
      const defaultAC = accountingCentersData.find((ac) => ac.is_default === true);
      if (defaultAC) {
        setDefaultAC(defaultAC);
      }

      // Add type property from acType
      const totalsWithType = { ...totals, type: String(acType).toUpperCase() as ACType };

      setTotalBudget(totalsWithType);
      setAccountingCenters(accountingCentersData);
    } catch (error) {
      console.error('Error fetching accounting center details:', error);
    }
  };

  const fetchBudgetInvoices = async () => {
    if (budgetId) {
      try {
        const { data: invoicesData } = await invoiceService.getInvoices({
          page: 1,
          limit: 12,
          budget_id: budgetId,
          invoice_category: activeInvoiceTab === 'All' ? undefined : activeInvoiceTab.toUpperCase(),
          type: undefined
        });
        const filteredInvoices =
          invoicesData.data && invoicesData.data.length > 0
            ? invoicesData.data.filter((invoice: any) => invoice.is_draft === false)
            : [];
        setInvoices(filteredInvoices || []);
      } catch (error) {
        console.error('Error fetching budget invoices:', error);
      } finally {
        setLoadingInvoices(false);
      }
    }
  };

  const fetchBudgetWorkActivities = async () => {
    if (!budgetId) return;
    try {
      setLoadingWorkActivities(true);
      const response = await workActivityService.getWorkActivities(budgetId, {
        type: 'all',
        page: 1,
        limit: 8
      });
      setWorkActivities(Array.isArray(response.data) ? response.data : []);
      setTotalWorkActivityCount(response.totalCount ?? 0);
    } catch (error) {
      console.error('Error fetching budget work activities:', error);
      setWorkActivities([]);
      setTotalWorkActivityCount(0);
    } finally {
      setLoadingWorkActivities(false);
    }
  };

  useEffect(() => {
    fetchBudgetInvoices();
  }, [activeInvoiceTab, budgetId]);

  useEffect(() => {
    fetchAccountCenterDetails();
  }, [acType]);

  useEffect(() => {
    fetchBudgetWorkActivities();
  }, [budgetId]);

  const handleEditClick = () => {
    navigate(PAGE.ACCOUNTING_CENTER.children.ADJUST, { state: { acCenter: totalBudget, accountCenters } });
  };

  const { btevChartData, btevRemainingPercent, btevRemainingAmount, acrtChartData } = calculateChartData(totalBudget);
  const btevChartConfig = getBtevChartConfig('secondary');
  const { acrtChartConfig } = getChartConfigs('secondary');

  const handleAddClick = () => {
    navigate(PAGE.ACCOUNTING_CENTER.children.ADD, { state: { defaultAC } });
  };

  return (
    <>
      <MobileAppBar
        title={<span className="capitalize">{acType?.split('_')[0]} Overview</span>}
        onRightIconClick={() => {
          setCurrentStep(0);
          setIsOpen(true);
        }}
        rightIcon={infoIcon}
        rightTwoIcon={canAdjustAccountingCenter ? editIcon : undefined}
        onRightTwoIconClick={canAdjustAccountingCenter ? handleEditClick : undefined}
      />

      <div className="h-full w-full min-w-0 p-4 md:p-0 flex flex-col xl:flex-row items-stretch justify-between gap-9 md:gap-4">
        {/* Main - ACMEV & ACRT */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          <div className="rounded-[16px] md:border md:bg-white md:p-4">
            <header className="flex items-start justify-between gap-4">
              <h2 className="w-[77%] mx-auto text-center text-base font-semibold uppercase md:text-left md:mx-0 md:w-fit md:text-2xl">
                {acType?.split('_')[0]} ACCOUNTING CENTERS
              </h2>
              <div className="hidden md:flex flex-col items-end gap-3">
                <button
                  onClick={() => {
                    setCurrentStep(0);
                    setIsOpen(true);
                  }}
                  className="hidden md:block"
                >
                  <img className="w-[28px] h-[28px]" src={infoIcon} alt="vault icon" />
                </button>

                <Tabs defaultValue="percentage" value={graphViewMode} onValueChange={setGraphViewMode}>
                  <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                    <TabsTrigger value="percentage" className="group md:py-[0.3rem] min-w-[36px]">
                      <img
                        src={percentageIcon}
                        alt="percentage-icon"
                        width={13}
                        height={17}
                        className="group-data-[state=active]:brightness-[100]"
                      />
                    </TabsTrigger>
                    <TabsTrigger value="euro" className="group md:py-[0.3rem] min-w-[36px]">
                      <img
                        src={euroIcon}
                        alt="euro-icon"
                        width={12}
                        height={12}
                        className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                      />
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </header>

            {/* Mobile View Graph Header */}
            <div className="flex items-center justify-between gap-4 mt-5 md:hidden">
              <p className="text-primary font-bold">Graph View</p>

              <Tabs defaultValue="percentage" value={graphViewMode} onValueChange={setGraphViewMode}>
                <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                  <TabsTrigger value="percentage" className="group md:py-[0.3rem] min-w-[36px]">
                    <img
                      src={percentageIcon}
                      alt="percentage-icon"
                      width={13}
                      height={17}
                      className="group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                  <TabsTrigger value="euro" className="group md:py-[0.3rem] min-w-[36px]">
                    <img
                      src={euroIcon}
                      alt="euro-icon"
                      width={12}
                      height={12}
                      className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Graphs (Mobile + Web) */}
            <div className="flex items-start justify-between gap-3 mt-3">
              <PieGraph
                title="Remaining BTEV"
                titleValue={(() => {
                  if (graphViewMode === 'percentage') {
                    return `${btevRemainingPercent}%`;
                  }

                  return `€${btevRemainingAmount.toLocaleString()}`;
                })()}
                chartConfig={btevChartConfig}
                chartData={btevChartData || []}
                className="bg-white rounded-[16px] p-[0.85rem] flex-1 md:border border-lightGray md:py-4 md:flex md:items-center md:justify-center md:flex-col md:min-h-[279px]"
                outerRadius={isMobileView ? 60 : 64}
                minHeight={isMobileView ? 120 : 140}
              />

              <PieGraph
                title="Achieved ACRT"
                titleValue={(() => {
                  if (graphViewMode === 'percentage') {
                    const achievedItem = acrtChartData && acrtChartData.find((item) => item.key === 'achieved');
                    return achievedItem ? `${achievedItem.value}%` : '0%';
                  } else {
                    // Euro mode - display the actual achieved value
                    const acrtAchieved = totalBudget.acrt_achieved;
                    return `€${acrtAchieved.toLocaleString()}`;
                  }
                })()}
                chartConfig={acrtChartConfig}
                chartData={acrtChartData || []}
                variant="primary"
                className="bg-white rounded-[16px] p-[0.85rem] flex-1 md:border border-lightGray md:py-4 md:flex md:items-center md:justify-center md:flex-col md:min-h-[279px]"
                outerRadius={isMobileView ? 60 : 64}
                minHeight={isMobileView ? 120 : 140}
              />
            </div>
          </div>

          {/* Recent Invoice (Web) */}
          <div className="hidden flex-1 min-w-0 md:flex flex-col border bg-white rounded-[16px] p-4">
            <header className="flex items-center justify-between gap-4">
              <Tabs value={activeContentTab} onValueChange={(value) => setActiveContentTab(value as ContentTab)}>
                <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                  <TabsTrigger value="invoice" className="group md:py-[0.3rem] px-3">
                    <h4 className="flex items-center gap-2.5">
                      <img src={invoiceIcon} alt="invoice-icon" className="group-data-[state=active]:hidden" />
                      <img
                        src={invoiceWhiteIcon}
                        alt="invoice-icon"
                        className="hidden group-data-[state=active]:block"
                      />
                      <span className="font-bold text-primary group-data-[state=active]:text-white">
                        Recent Invoice
                      </span>
                    </h4>
                  </TabsTrigger>
                  <TabsTrigger value="work-activity" className="group md:py-[0.3rem] px-3">
                    <h4 className="flex items-center gap-2.5">
                      <img
                        src={workActivityIcon}
                        alt="work-activity-icon"
                        className="group-data-[state=active]:hidden"
                      />
                      <img
                        src={workActivityWhiteIcon}
                        alt="work-activity-icon"
                        className="hidden group-data-[state=active]:block"
                      />
                      <span className="font-bold text-primary group-data-[state=active]:text-white">Work Activity</span>
                    </h4>
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {activeContentTab === 'invoice' && invoices.length > 0 && (
                <Button
                  variant="text"
                  onClick={() => navigate(`/budgets/${budgetId}/invoices`)}
                  className="py-0 h-[30px]"
                >
                  See All
                </Button>
              )}
              {activeContentTab === 'work-activity' && totalWorkActivityCount > 7 && (
                <Button
                  variant="text"
                  onClick={() => navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity`)}
                  className="py-0 h-[30px]"
                >
                  See All
                </Button>
              )}
            </header>
            {activeContentTab === 'invoice' &&
              (invoices.length > 0 || activeInvoiceTab !== 'All' ? (
                <div className="flex flex-col gap-2 min-w-0 bg-lightPurple rounded-[16px] p-4 pt-2 mt-4 md:mt-3 md:p-0 md:bg-white">
                  <TabPills
                    options={['All', 'Expense', 'Revenue']}
                    value={activeInvoiceTab}
                    onValueChange={setActiveInvoiceTab}
                  />

                  <InvoicesTable
                    invoices={invoices.slice(0, 10)}
                    setSelectedInvoice={() => {}}
                    onInvoiceSelect={handleInvoiceSelect}
                  />
                </div>
              ) : (
                <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
                  <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                    <img src={noInvoiceIcon} alt="no invoice icon" className="md:w-[40px] md:h-[40px]" />
                    <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                      <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                        There are no Invoice to show.
                      </span>{' '}
                      <span className="md:text-secondary md:text-base md:font-medium">
                        {' '}
                        Please create a new invoice to allocate budget!
                      </span>
                    </span>
                  </div>
                  {canCreateInvoice && (
                    <Button
                      onClick={() =>
                        navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
                          state: { from: 'budget', budgetId }
                        })
                      }
                      variant="primary-reverse"
                      className="rounded-full font-semibold px-5 text-base"
                    >
                      Create an Invoice
                    </Button>
                  )}
                </div>
              ))}
            {activeContentTab === 'work-activity' &&
              (workActivities.length > 0 || loadingWorkActivities ? (
                <div className="flex flex-col gap-2 min-w-0 bg-lightPurple rounded-[16px] p-4 pt-2 mt-4 md:mt-3 md:p-0 md:bg-white">
                  <WorkActivityTable
                    workActivities={workActivities.slice(0, 7)}
                    loading={loadingWorkActivities}
                    budgetId={budgetId}
                    onWorkActivitySelect={(wa) => {
                      if (budgetId) {
                        navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/${wa._id}`);
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
                  <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                    <img src={noWorkActivityIcon} alt="no work activity icon" className="md:w-[40px] md:h-[40px]" />
                    <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                      <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                        There are no work activity to show.
                      </span>{' '}
                      <span className="md:text-secondary md:text-base md:font-medium">
                        You can create work activity to request a payment
                      </span>
                    </span>
                  </div>
                  <Button
                    onClick={() =>
                      navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/create`, {
                        state: { from: 'budget', budgetId }
                      })
                    }
                    variant="primary-reverse"
                    className="rounded-full font-semibold px-5 text-base"
                  >
                    Create a Work Activity
                  </Button>
                </div>
              ))}
          </div>
        </div>

        {/* Sidebar: flex-1 fills column layout below xl; xl:flex-none + max-w so main column (min-w-0) keeps room for invoice table */}
        <div className="flex-1 min-w-0 w-full xl:flex-none xl:shrink-0 xl:max-w-[400px] rounded-[16px] md:border md:bg-white md:p-4 font-medium md:font-bold text-xs md:text-base md:overflow-y-auto md:min-h-[300px]">
          <header className="flex items-center justify-between gap-5">
            <span>All Department AC</span>

            {canAdjustAccountingCenter && (
              <button onClick={handleEditClick} title="Edit" className="hidden md:block">
                <img className="w-[28px] h-[28px]" src={editIcon} alt="vault icon" />
              </button>
            )}
          </header>

          <Accordion
            type="multiple"
            value={openItems}
            onValueChange={setOpenItems}
            className="flex flex-col gap-4 mt-5"
          >
            {accountCenters && accountCenters?.length > 0 ? (
              accountCenters.map((ac: any) => (
                <ACItemAccordion
                  key={ac._id}
                  ac={ac}
                  budget={budget}
                  acCenter={totalBudget}
                  graphViewMode={graphViewMode}
                  open={openItems.includes(ac._id)}
                  onToggle={() => {
                    setOpenItems((prev) =>
                      prev.includes(ac._id) ? prev.filter((id) => id !== ac._id) : [...prev, ac._id]
                    );
                  }}
                />
              ))
            ) : (
              <p className="text-sm text-secondary text-center py-4 font-normal">No accounting center found</p>
            )}
          </Accordion>
        </div>

        {/* Recent Invoice / Work Activity (Mobile) */}
        <div className="flex-1 pb-8 md:hidden flex flex-col">
          <Tabs value={activeContentTab} onValueChange={(value) => setActiveContentTab(value as ContentTab)}>
            <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem] w-full grid grid-cols-2">
              <TabsTrigger value="invoice" className="group py-[0.3rem] px-3 w-full">
                <h4 className="flex items-center justify-center gap-2.5">
                  <img src={invoiceIcon} alt="invoice-icon" className="group-data-[state=active]:hidden" />
                  <img src={invoiceWhiteIcon} alt="invoice-icon" className="hidden group-data-[state=active]:block" />
                  <span className="font-bold text-primary group-data-[state=active]:text-white">Recent Invoice</span>
                </h4>
              </TabsTrigger>
              <TabsTrigger value="work-activity" className="group py-[0.3rem] px-3 w-full">
                <h4 className="flex items-center justify-center gap-2.5">
                  <img src={workActivityIcon} alt="work-activity-icon" className="group-data-[state=active]:hidden" />
                  <img
                    src={workActivityWhiteIcon}
                    alt="work-activity-icon"
                    className="hidden group-data-[state=active]:block"
                  />
                  <span className="font-bold text-primary group-data-[state=active]:text-white">Work Activity</span>
                </h4>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {activeContentTab === 'invoice' &&
            (invoices.length > 0 || activeInvoiceTab !== 'All' ? (
              <div className="flex flex-col gap-2 bg-lightPurple rounded-[16px] p-4 py-5 mt-4">
                <TabPills
                  options={['All', 'Expense', 'Revenue']}
                  value={activeInvoiceTab}
                  onValueChange={setActiveInvoiceTab}
                />

                <InvoicesTable
                  invoices={invoices.slice(0, 10)}
                  setSelectedInvoice={() => {}}
                  onInvoiceSelect={handleInvoiceSelect}
                />
              </div>
            ) : (
              <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4">
                <div className="flex flex-col items-center justify-center">
                  <img src={noInvoiceIcon} alt="no invoice icon" />
                  <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px]">
                    <span className="block mb-[-3px]">There are no Invoice to show.</span>{' '}
                    <span>Please create a new invoice to allocate budget!</span>
                  </span>
                </div>
                {canCreateInvoice && (
                  <Button
                    onClick={() =>
                      navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
                        state: { from: 'budget', budgetId }
                      })
                    }
                    variant="primary-reverse"
                    className="rounded-full font-semibold px-5 text-base"
                  >
                    Create an Invoice
                  </Button>
                )}
              </div>
            ))}

          {activeContentTab === 'work-activity' &&
            (workActivities.length > 0 || loadingWorkActivities ? (
              <div className="flex flex-col gap-2 bg-lightPurple rounded-[16px] p-4 py-5 mt-4">
                <WorkActivityTable
                  workActivities={workActivities.slice(0, 7)}
                  loading={loadingWorkActivities}
                  budgetId={budgetId}
                  onWorkActivitySelect={(wa) => {
                    if (budgetId) {
                      navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/${wa._id}`);
                    }
                  }}
                />
              </div>
            ) : (
              <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4">
                <div className="flex flex-col items-center justify-center">
                  <img src={noWorkActivityIcon} alt="no work activity icon" />
                  <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px]">
                    <span className="block mb-[-3px]">There are no work activity to show.</span>{' '}
                    <span>You can create work activity to request a payment</span>
                  </span>
                </div>
                <Button
                  onClick={() =>
                    navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/create`, {
                      state: { from: 'budget', budgetId }
                    })
                  }
                  variant="primary-reverse"
                  className="rounded-full font-semibold px-5 text-base"
                >
                  Create a Work Activity
                </Button>
              </div>
            ))}
        </div>

        {canCreateAccountingCenter && (
          <div className="rounded-[22px] flex-1 flex items-center justify-center absolute bottom-[26px] right-[36px] shadow-[0px_4px_4px_0px_#00000040]">
            <Button onClick={handleAddClick} className="rounded-full px-6 w-[64px] h-[64px] rounded-[22px]">
              <span className="text-white text-[2.5rem] font-light">+</span>
            </Button>
          </div>
        )}

        {/* Explanatory Modal */}
        {isOpen && (
          <ExplanatoryModal
            isOpen={isOpen}
            steps={steps}
            currentStep={currentStep}
            buttonText="Next"
            onButtonClick={() => {
              setCurrentStep(currentStep + 1);
            }}
            onClose={() => {
              setIsOpen(false);
            }}
            onStepChange={setCurrentStep}
          />
        )}
      </div>
    </>
  );
};

export default AccountingCentersPage;
